import React, { useState, useEffect } from 'react';
import { Search, Filter, Plus, Download, Edit, Trash2, Eye, MoreHorizontal, ChevronDown, Zap, Brain, RefreshCw, MessageCircle, Database, Sparkles } from 'lucide-react';
import AIService, { LeadScore } from '../../services/aiService';
import NetlifyAIService, { EnhancedLeadScore } from '../../services/ai/netlifyAIService';
import unifiedDataService from '../../services/unifiedDataService';
import { sixSenseApi, type SixSenseEnrichmentResponse, type SixSenseCompanyEnrichmentResponse } from '../../services/api/sixsense.api';

interface Lead {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: 'New' | 'Contacted' | 'Qualified' | 'Lost';
  source: string;
  value: number;
  createdAt: string;
  avatar?: string;
  location?: string;
  // 6sense enrichment data
  enrichment?: {
    jobTitle?: string;
    seniority?: string;
    department?: string;
    companyDomain?: string;
    industry?: string;
    employeeCount?: number;
    revenue?: number;
    technographics?: string[];
    enrichedAt?: string;
    confidence?: number;
  };
}

const mockLeads: Lead[] = [
  {
    id: '1',
    name: 'John Smith',
    company: 'Acme Corp',
    email: 'john@acme.com',
    phone: '+1 (555) 123-4567',
    status: 'New',
    source: 'Website',
    value: 45000,
    createdAt: '2024-01-15'
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    company: 'Tech Solutions',
    email: 'sarah@techsol.com',
    phone: '+1 (555) 987-6543',
    status: 'Qualified',
    source: 'Referral',
    value: 67500,
    createdAt: '2024-01-14'
  },
  {
    id: '3',
    name: 'Michael Brown',
    company: 'Global Industries',
    email: 'm.brown@global.com',
    phone: '+1 (555) 456-7890',
    status: 'Contacted',
    source: 'LinkedIn',
    value: 23000,
    createdAt: '2024-01-13'
  },
];

interface LeadsCRUDProps {
  onAnalyzeWithAI?: (lead: Lead) => void;
  onSendToSlack?: (lead: Lead) => void;
}

export const LeadsCRUDInterface: React.FC<LeadsCRUDProps> = ({ onAnalyzeWithAI, onSendToSlack }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [leadScores, setLeadScores] = useState<Record<string, LeadScore>>({});
  const [isScoring, setIsScoring] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [enrichingLeads, setEnrichingLeads] = useState<Set<string>>(new Set());
  const itemsPerPage = 10;

  // Load real data on component mount
  useEffect(() => {
    loadRealData();
  }, []);

  const loadRealData = async () => {
    setIsLoading(true);
    try {
      // Use unified data service - single source of truth
      const leads = await unifiedDataService.getLeads();
      setLeads(leads);
    } catch (error) {
      console.error('Failed to load real leads data:', error);
      // Fallback to mock data
      setLeads(mockLeads);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    try {
      // Use unified data service refresh method
      const leads = await unifiedDataService.refreshLeads();
      setLeads(leads);
    } catch (error) {
      console.error('Failed to refresh leads data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'New':
        return 'bg-blue-100 text-blue-800';
      case 'Contacted':
        return 'bg-yellow-100 text-yellow-800';
      case 'Qualified':
        return 'bg-green-100 text-green-800';
      case 'Lost':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDelete = (id: string) => {
    setLeads(leads.filter(lead => lead.id !== id));
  };

  const handleEnrichLead = async (lead: Lead) => {
    const leadId = lead.id;
    setEnrichingLeads(prev => new Set(prev).add(leadId));

    try {
      const enrichmentData = await sixSenseApi.enrichLead(lead.email, lead.company);

      if (enrichmentData.person || enrichmentData.company) {
        const updatedLead = {
          ...lead,
          enrichment: {
            jobTitle: enrichmentData.person?.person.jobTitle,
            seniority: enrichmentData.person?.person.seniority,
            department: enrichmentData.person?.person.department,
            companyDomain: enrichmentData.company?.company.domain,
            industry: enrichmentData.company?.company.industry,
            employeeCount: enrichmentData.company?.company.employeeCount,
            revenue: enrichmentData.company?.company.revenue,
            technographics: enrichmentData.company?.company.technographics,
            enrichedAt: new Date().toISOString(),
            confidence: Math.max(
              enrichmentData.person?.confidence || 0,
              enrichmentData.company?.confidence || 0
            )
          }
        };

        setLeads(prevLeads =>
          prevLeads.map(l => l.id === leadId ? updatedLead : l)
        );
      }
    } catch (error) {
      console.error('Failed to enrich lead:', error);
    } finally {
      setEnrichingLeads(prev => {
        const newSet = new Set(prev);
        newSet.delete(leadId);
        return newSet;
      });
    }
  };

  const handleAIScoring = async () => {
    setIsScoring(true);
    try {
      const netlifyAI = new NetlifyAIService();
      const enhancedScores = await netlifyAI.scoreLeadsEnhanced(filteredLeads);
      const scoreMap = enhancedScores.reduce((acc, score) => ({
        ...acc,
        [score.leadId]: {
          leadId: score.leadId,
          score: score.score,
          confidence: score.confidenceScore,
          provider: score.aiProviderUsed,
          breakdown: score.enrichmentAnalysis
        }
      }), {});
      setLeadScores(scoreMap);
      setShowAIInsights(true);
    } catch (error) {
      console.error('Failed to score leads with enhanced AI:', error);
      // Fallback to basic scoring
      try {
        const scores = await AIService.scoreLeads(filteredLeads);
        const scoreMap = scores.reduce((acc, score) => ({
          ...acc,
          [score.leadId]: score
        }), {});
        setLeadScores(scoreMap);
        setShowAIInsights(true);
      } catch (fallbackError) {
        console.error('Fallback scoring also failed:', fallbackError);
      }
    } finally {
      setIsScoring(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-64"></div>
          <div className="h-4 bg-gray-200 rounded w-96"></div>
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leads Management</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage your sales leads and track conversion progress • {leads.length} real leads from API
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <button
              onClick={handleAIScoring}
              disabled={isScoring}
              className="inline-flex items-center px-4 py-2 border border-netlify-coral-300 rounded-lg shadow-sm text-sm font-medium text-netlify-coral-700 bg-netlify-coral-50 hover:bg-netlify-coral-100 disabled:opacity-50 transition-all duration-200"
            >
              {isScoring ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-netlify-coral-600 mr-2"></div>
              ) : (
                <Brain className="w-4 h-4 mr-2" />
              )}
              {isScoring ? 'Scoring...' : '🤖 AI Score Leads'}
            </button>
            <button
              onClick={handleRefreshData}
              disabled={isRefreshing}
              className="inline-flex items-center px-4 py-2 border border-netlify-teal-300 rounded-lg shadow-sm text-sm font-medium text-netlify-teal-700 bg-white hover:bg-netlify-teal-50 transition-all duration-200 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-netlify-teal-300 rounded-lg shadow-sm text-sm font-medium text-netlify-teal-700 bg-white hover:bg-netlify-teal-50 transition-all duration-200">
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
            <button className="netlify-button-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-6">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              className="appearance-none bg-white border border-gray-300 rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1); // Reset to first page when filtering
              }}
            >
              <option value="all">All Status</option>
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
              <option value="Qualified">Qualified</option>
              <option value="Lost">Lost</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Additional Filters */}
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <Filter className="w-4 h-4 mr-2" />
            More Filters
          </button>
        </div>

        {/* Results Summary */}
        <div className="mb-4">
          <p className="text-sm text-gray-700">
            Showing {paginatedLeads.length} of {filteredLeads.length} leads
          </p>
        </div>

        {/* Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                {Object.keys(leadScores).length > 0 && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AI Score
                  </th>
                )}
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-700">
                            {lead.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                        <div className="text-sm text-gray-500">
                          {lead.company}
                          {lead.enrichment?.jobTitle && (
                            <span className="text-blue-600 ml-2">• {lead.enrichment.jobTitle}</span>
                          )}
                        </div>
                        {lead.enrichment && (
                          <div className="text-xs text-green-600 flex items-center mt-1">
                            <Database className="w-3 h-3 mr-1" />
                            <span>6sense Enriched ({lead.enrichment.confidence || 0}% confidence)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{lead.email}</div>
                    <div className="text-sm text-gray-500">{lead.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(lead.status)}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${lead.value.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lead.source}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(lead.createdAt).toLocaleDateString()}
                  </td>
                  {Object.keys(leadScores).length > 0 && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      {leadScores[lead.id] ? (
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreColor(leadScores[lead.id].score)}`}>
                            {leadScores[lead.id].score}
                          </span>
                          <Zap className="w-3 h-3 text-purple-500" />
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button className="text-blue-600 hover:text-blue-900">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onAnalyzeWithAI && onAnalyzeWithAI(lead)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Analyze with AI"
                      >
                        <Brain className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onSendToSlack && onSendToSlack(lead)}
                        className="text-green-600 hover:text-green-900"
                        title="Send to Slack"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEnrichLead(lead)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Enrich with 6sense"
                        disabled={enrichingLeads.has(lead.id)}
                      >
                        {enrichingLeads.has(lead.id) ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Database className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(lead.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    const isCurrentPage = page === currentPage;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          isCurrentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsCRUDInterface;